import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import CountryCodeSelect from '../components/CountryCodeSelect';
import { validLocalPhone, composePhone, localPlaceholder } from '../utils/phone';

const RED = '#8C1F1F';
const GOLD = '#B8892A';
const MUTED = '#6B5E4E';
const GREEN = '#2A6645';
const BD = '#E8E0D0';
const BG = '#F8F5F0';

function PwStrength({ password }) {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Za-z]/.test(password) && /\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (password.length >= 12) score++;
  const cls = score <= 1 ? RED : score <= 2 ? GOLD : GREEN;
  const label = password.length === 0 ? '' : score === 0 ? '需含字母+数字，至少8位' : ['', '弱（需含字母+数字）', '中等', '较强', '强'][score];
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', gap: 3, marginBottom: 3 }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i < score ? cls : BD, transition: 'background 0.3s' }} />
        ))}
      </div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: score > 0 ? cls : '#A8998A' }}>{label}</div>
    </div>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // 归因优先级：URL ?ref > 短链落地存储的 ref_attribution（30天内） > 手动输入
  const [refCode, setRefCode] = useState(() => {
    const fromUrl = searchParams.get('ref');
    if (fromUrl) return fromUrl;
    try {
      const saved = JSON.parse(localStorage.getItem('ref_attribution') || 'null');
      if (saved?.code && Date.now() - saved.ts < 30 * 24 * 3600 * 1000) return saved.code;
    } catch { /* 解析失败忽略 */ }
    return '';
  });
  const [referralValid, setReferralValid] = useState(null);

  const [phoneRegEnabled, setPhoneRegEnabled] = useState(true);
  const [mode, setMode] = useState('phone');
  const [countryCode, setCountryCode] = useState('+86');
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [emailSent, setEmailSent] = useState(false);
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const timerRef = useRef(null);

  useEffect(() => {
    api.get('/settings').then(res => {
      const enabled = String(res.data?.phone_registration_enabled) === '1';
      setPhoneRegEnabled(enabled);
      if (!enabled) setMode('email');
    }).catch(() => { setPhoneRegEnabled(false); setMode('email'); });
  }, []);

  useEffect(() => {
    if (refCode && refCode.length >= 4) {
      const timer = setTimeout(() => {
        api.get(`/referral/validate/${refCode}`).then(res => {
          setReferralValid(res.data.valid ? res.data : false);
        }).catch(() => setReferralValid(false));
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setReferralValid(null);
    }
  }, [refCode]);

  const phoneValid = validLocalPhone(countryCode, phone);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // 发码冷却：结束时刻(ms)存 localStorage，刷新/跨页不重置，防绕过冷却狂点
  const CD_KEY = 'sms_cd_until';
  const runCountdown = (until) => {
    if (timerRef.current) clearInterval(timerRef.current);
    const upd = () => {
      const r = Math.max(0, Math.ceil((until - Date.now()) / 1000));
      setCountdown(r);
      if (r <= 0) { clearInterval(timerRef.current); try { localStorage.removeItem(CD_KEY); } catch {} }
    };
    upd();
    timerRef.current = setInterval(upd, 1000);
  };
  const startCountdown = () => {
    const until = Date.now() + 60 * 1000;
    try { localStorage.setItem(CD_KEY, String(until)); } catch {}
    runCountdown(until);
  };
  // 进入页面时恢复未结束的冷却
  useEffect(() => {
    try {
      const until = parseInt(localStorage.getItem(CD_KEY) || '0', 10);
      if (until > Date.now()) runCountdown(until);
    } catch {}
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendSMS = async () => {
    if (!phoneValid || countdown > 0) return;
    setError('');
    try {
      // phone 统一为存储格式（中国裸号 / 国际 E.164），与提交注册时的格式一致
      await api.post('/auth/send-sms', { phone: composePhone(countryCode, phone), country_code: countryCode });
      startCountdown();
    } catch (err) {
      setError(err.response?.data?.error || t('common.failed'));
    }
  };

  const sendEmailCodeFn = async () => {
    if (!emailValid || countdown > 0) return;
    setError('');
    try {
      await api.post('/auth/send-email-code', { email });
      startCountdown();
      setEmailSent(true);
    } catch (err) {
      setError(err.response?.data?.error || t('common.failed'));
    }
  };

  const handleSubmit = async () => {
    setError('');
    if (mode === 'phone') {
      if (!phoneValid) { setError(t('auth.phonePlaceholder')); return; }
      if (!smsCode.trim()) { setError(t('auth.codePlaceholder')); return; }
    } else {
      if (!emailValid) { setError(t('auth.emailPlaceholder')); return; }
      if (!emailCode.trim()) { setError(t('auth.codePlaceholder')); return; }
    }
    if (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      setError(t('auth.passwordFormatError')); return;
    }
    if (!agree) { setError(t('auth.agreeTerms')); return; }

    if (mode === 'email') {
      try {
        await api.post('/auth/check-email-code', { email, code: emailCode });
      } catch (err) {
        setError(err.response?.data?.error || '验证码校验失败'); return;
      }
    }

    setSubmitting(true);
    try {
      const res = await api.post('/auth/register', {
        registerMode: mode,
        phone: mode === 'phone' ? composePhone(countryCode, phone) : undefined,
        country_code: mode === 'phone' ? countryCode : undefined,
        email: mode === 'email' ? email : undefined,
        smsCode: mode === 'phone' ? smsCode : undefined,
        emailCode: mode === 'email' ? emailCode : undefined,
        password,
        referralCode: refCode || undefined,
      });
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
      }
      localStorage.removeItem('ref_attribution');
      navigate('/auction-hub');
    } catch (err) {
      setError(err.response?.data?.error || t('common.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Field styles (旧版视觉) ──
  const inputStyle = {
    width: '100%', padding: '10px 14px',
    border: `1px solid ${BD}`, borderRadius: 2,
    fontFamily: 'var(--sans)', fontSize: 13, color: '#110E0A',
    background: BG, outline: 'none', transition: 'all 0.15s',
    boxSizing: 'border-box',
  };
  const focusStyle = (e) => { e.target.style.borderColor = RED; e.target.style.boxShadow = '0 0 0 3px rgba(140,31,31,0.06)'; };
  const blurStyle = (e) => { e.target.style.borderColor = BD; e.target.style.boxShadow = 'none'; };

  const groupBox = { display: 'flex', border: `1px solid ${BD}`, borderRadius: 2, overflow: 'hidden' };
  const groupInput = {
    flex: 1, minWidth: 0, padding: '10px 14px', border: 'none', outline: 'none',
    fontFamily: 'var(--sans)', fontSize: 13, color: '#110E0A', background: BG,
  };
  const codeBtn = {
    padding: '10px 16px', background: '#EDE8E0', fontFamily: 'var(--mono)',
    fontSize: 10, color: countdown > 0 ? '#A8998A' : RED, cursor: countdown > 0 ? 'not-allowed' : 'pointer',
    border: 'none', borderLeft: `1px solid ${BD}`, whiteSpace: 'nowrap', flexShrink: 0,
  };
  const pwdToggle = {
    padding: '10px 14px', background: '#EDE8E0', fontFamily: 'var(--mono)',
    fontSize: 10, color: GOLD, cursor: 'pointer', border: 'none', borderLeft: `1px solid ${BD}`,
    whiteSpace: 'nowrap', flexShrink: 0,
  };
  const fieldLabel = { fontSize: 12, color: MUTED, marginBottom: 6 };
  const optionalHint = { fontSize: 10, color: '#A8998A' };
  const star = <span style={{ color: RED, fontSize: 10 }}>*</span>;

  return (
    <div style={{ fontFamily: 'var(--sans)', background: BG, color: '#110E0A', minHeight: '100vh' }}>
      {/* ── Header ── */}
      <header style={{ background: '#fff', borderBottom: `1px solid ${BD}`, padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => navigate('/')}>
          <div style={{ width: 32, height: 32, borderRadius: 4, background: RED, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F5E6D0', fontSize: 10, fontWeight: 700, lineHeight: 1.1, textAlign: 'center', letterSpacing: 1 }}>云川<br/>中古</div>
          <span style={{ fontFamily: 'var(--serif)', fontSize: 17, color: GOLD, letterSpacing: 4 }}>云川中古</span>
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: '#A8998A' }}>
          {t('auth.hasAccount')}<span onClick={() => navigate('/login')} style={{ color: GOLD, cursor: 'pointer' }}>{t('auth.goLogin')}</span>
        </div>
      </header>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '40px 20px 60px' }}>

        {/* ── Main Card ── */}
        <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 4, overflow: 'hidden', boxShadow: '0 2px 12px rgba(26,18,8,0.10)' }}>

          {/* Card Header */}
          <div style={{ padding: '20px 28px 16px', borderBottom: `2px solid ${RED}`, background: 'linear-gradient(135deg,#F8F5F0,#fff)' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: 3, color: RED, marginBottom: 6 }}>REGISTRATION · {t('auth.register')}</div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 600, color: '#110E0A', letterSpacing: 2 }}>创建您的账号</div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
              {mode === 'phone' ? '填写手机号与验证码，设置登录密码' : '填写邮箱与验证码，设置登录密码'}
            </div>
          </div>

          {/* Card Body */}
          <div style={{ padding: 28 }}>

            {/* WeChat quick login */}
            {mode === 'phone' && (
              <div style={{ marginBottom: 20 }}>
                <button
                  onClick={() => navigate('/login?wx=1')}
                  style={{
                    width: '100%', padding: '11px 0', background: '#07C160',
                    border: 'none', borderRadius: 2, cursor: 'pointer',
                    fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 500, color: '#fff',
                  }}
                >
                  🟢 微信快捷登录注册
                </button>
                <div style={{ display: 'flex', alignItems: 'center', margin: '20px 0 22px', gap: 16 }}>
                  <div style={{ flex: 1, height: 1, background: BD }} />
                  <span style={{ fontSize: 10, color: '#A8998A', whiteSpace: 'nowrap', fontFamily: 'var(--mono)', letterSpacing: 1 }}>{t('common.or')}</span>
                  <div style={{ flex: 1, height: 1, background: BD }} />
                </div>
              </div>
            )}

            {/* Referral banner */}
            {refCode && referralValid && (
              <div style={{
                padding: '12px 16px', borderRadius: 4, marginBottom: 16,
                background: 'linear-gradient(135deg, rgba(42,102,69,0.08), rgba(184,137,42,0.08))',
                border: '1px solid rgba(42,102,69,0.2)', display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ fontSize: 20 }}>🎁</span>
                <div>
                  <div style={{ fontSize: 13, color: GREEN, fontWeight: 500 }}>{referralValid.referrerName} 邀请您加入</div>
                  <div style={{ fontSize: 11, color: MUTED }}>注册即可获得新人奖励</div>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div style={{
                padding: '10px 14px', borderRadius: 2, marginBottom: 16,
                background: 'rgba(140,31,31,0.07)', border: `1px solid rgba(140,31,31,0.2)`,
                color: RED, fontSize: 12,
              }}>
                ⚠ {error}
              </div>
            )}

            {/* Mode selector (旧版卡片式单选) */}
            <div style={{ marginBottom: 22, display: 'flex', gap: 12 }}>
              {[
                { val: 'phone', label: t('auth.phone') + t('auth.register'), icon: '📱' },
                { val: 'email', label: t('auth.email') + t('auth.register'), icon: '✉️' },
              ].map(opt => {
                const disabled = opt.val === 'phone' && !phoneRegEnabled;
                const active = mode === opt.val;
                return (
                  <label key={opt.val} style={{
                    flex: 1, display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 14px', border: `1px solid ${active ? RED : BD}`,
                    borderRadius: 2, cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
                    background: disabled ? '#F0EDEA' : active ? 'rgba(140,31,31,0.05)' : BG,
                    opacity: disabled ? 0.45 : 1,
                  }}>
                    <input
                      type="radio" name="registerMode" value={opt.val}
                      checked={active}
                      disabled={disabled}
                      onChange={() => { if (!disabled) { setMode(opt.val); setError(''); setCountdown(0); clearInterval(timerRef.current); } }}
                      style={{ accentColor: RED, marginRight: 2 }}
                    />
                    <span style={{ fontSize: 13 }}>{opt.icon}</span>
                    <span style={{ fontSize: 13, color: active ? RED : MUTED, fontWeight: active ? 500 : 400 }}>{opt.label}</span>
                  </label>
                );
              })}
            </div>

            {/* ── Phone mode ── */}
            {mode === 'phone' && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <div style={fieldLabel}>{t('auth.phone')} {star}</div>
                  <div style={groupBox}>
                    <CountryCodeSelect value={countryCode} onChange={setCountryCode}
                      style={{ borderRight: `1px solid ${BD}`, padding: '0 10px' }} />
                    <input
                      type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                      placeholder={localPlaceholder(countryCode)} maxLength={11}
                      style={groupInput}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={fieldLabel}>{t('auth.verifyCode')} {star}</div>
                  <div style={groupBox}>
                    <input
                      value={smsCode} onChange={e => setSmsCode(e.target.value)}
                      placeholder={t('auth.codePlaceholder')} maxLength={6}
                      style={{ ...groupInput, letterSpacing: 4, fontFamily: 'var(--mono)' }}
                    />
                    <button onClick={sendSMS} disabled={!phoneValid || countdown > 0} style={codeBtn}>
                      {countdown > 0 ? `${countdown}s` : t('auth.sendCode')}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ── Email mode ── */}
            {mode === 'email' && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <div style={fieldLabel}>{t('auth.email')} {star}</div>
                  <div style={groupBox}>
                    <input
                      type="email" value={email} onChange={e => { setEmail(e.target.value); setEmailSent(false); }}
                      placeholder={t('auth.emailPlaceholder')}
                      style={groupInput}
                    />
                    <button onClick={sendEmailCodeFn} disabled={!emailValid || countdown > 0} style={codeBtn}>
                      {countdown > 0 ? `${countdown}s` : t('auth.sendCode')}
                    </button>
                  </div>
                  {emailSent && (
                    <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 4, background: '#F0FDF4', border: '1px solid #BBF7D0', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 14 }}>✅</span>
                      <span style={{ fontSize: 12, color: '#166534' }}>
                        验证码已发送至 <b>{email}</b>，请查收邮件（注意检查垃圾邮件文件夹）
                      </span>
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={fieldLabel}>{t('auth.verifyCode')} {star}</div>
                  <input
                    value={emailCode} onChange={e => setEmailCode(e.target.value)}
                    placeholder={t('auth.codePlaceholder')} maxLength={6}
                    style={{ ...inputStyle, letterSpacing: 4, fontFamily: 'var(--mono)' }}
                    onFocus={focusStyle} onBlur={blurStyle}
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={fieldLabel}>{t('auth.phone')} <span style={optionalHint}>（{t('common.optional')}，竞拍联系用）</span></div>
                  <div style={groupBox}>
                    <CountryCodeSelect value={countryCode} onChange={setCountryCode}
                      style={{ borderRight: `1px solid ${BD}`, padding: '0 10px' }} />
                    <input
                      type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                      placeholder={localPlaceholder(countryCode)} maxLength={11}
                      style={groupInput}
                    />
                  </div>
                </div>
              </>
            )}

            {/* ── Password — shared ── */}
            <div style={{ marginBottom: 20 }}>
              <div style={fieldLabel}>{t('auth.password')} {star} <span style={optionalHint}>（至少8位，含字母+数字）</span></div>
              <div style={groupBox}>
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="8-20位，包含字母和数字"
                  style={groupInput}
                />
                <button onClick={() => setShowPwd(!showPwd)} style={pwdToggle}>
                  {showPwd ? '隐藏' : '显示'}
                </button>
              </div>
              {password.length > 0 && <PwStrength password={password} />}
            </div>

            {/* ── Referral code ── */}
            <div style={{ marginBottom: 20 }}>
              <div style={fieldLabel}>邀请码 <span style={optionalHint}>（{t('common.optional')}，享新人奖励）</span></div>
              <input
                value={refCode} onChange={e => setRefCode(e.target.value.trim())}
                placeholder="如有邀请码请输入，可享新人奖励"
                style={inputStyle}
                onFocus={focusStyle} onBlur={blurStyle}
              />
              {refCode && referralValid === false && (
                <div style={{ marginTop: 6, fontSize: 12, color: RED }}>邀请码无效，请核实后重新输入</div>
              )}
            </div>

            {/* ── Agreement ── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 22 }}>
              <input
                type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)}
                style={{ marginTop: 2, cursor: 'pointer', accentColor: RED }}
              />
              <label style={{ fontSize: 12, color: MUTED, cursor: 'pointer', lineHeight: 1.6 }}>
                {t('auth.agreeTerms')} <span style={{ color: RED, cursor: 'pointer' }}>《{t('auth.terms')}》</span> 与 <span style={{ color: RED, cursor: 'pointer' }}>《{t('auth.privacy')}》</span>
              </label>
            </div>

            {/* Security note */}
            <div style={{ padding: '10px 14px', background: 'rgba(42,102,69,0.08)', border: '1px solid rgba(42,102,69,0.15)', borderRadius: 2, fontSize: 11, color: GREEN, marginBottom: 22 }}>
              🔒 您的信息经 TLS 1.3 加密传输，仅用于账号注册
            </div>

            {/* ── Submit ── */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                padding: '11px 28px', background: submitting ? '#A8998A' : RED,
                border: `1px solid ${submitting ? '#A8998A' : RED}`, borderRadius: 2,
                color: '#fff', fontFamily: 'var(--sans)', fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer',
                width: '100%',
              }}
            >
              {submitting ? t('common.loading') : t('auth.register')}
            </button>

          </div>
        </div>

        {/* Bottom link */}
        <div style={{ textAlign: 'center', marginTop: 24, fontFamily: 'var(--mono)', fontSize: 10, color: '#A8998A', letterSpacing: 1 }}>
          {t('auth.hasAccount')}
          <span onClick={() => navigate('/login')} style={{ color: GOLD, cursor: 'pointer' }}>{t('auth.goLogin')}</span>
        </div>
      </div>
    </div>
  );
}
